// Product-alias helpers, shared by the SMS agent (server) and the in-app product
// searches (client). Pure functions only — no server or React dependencies.
//
// Aliases are colloquial buyer terms mapped to a product ("romas" → "Roma Tomatoes
// 25lb"). Storage shape is ProductAlias[] on products.aliases (json): labeled objects
// or plain strings (plain = manual+active). Only 'active' entries resolve or match
// search; 'suggested' entries are agent-learned candidates awaiting admin approval and
// are inert everywhere until approved.
import type { ProductAlias, ProductAliasEntry, ProductAliasSource, ProductAliasStatus } from "./schema";

export const PRODUCT_ALIASES_MAX = 20; // per product
export const PRODUCT_ALIAS_TEXT_MAX = 60; // chars per alias

const SOURCES: ProductAliasSource[] = ["manual", "learned"];
const STATUSES: ProductAliasStatus[] = ["active", "suggested"];

/**
 * Canonical alias-text form: invisible format characters stripped (zero-widths,
 * bidi controls, BOM — buyer SMS text flows in here verbatim, and a byte-distinct
 * lookalike would dodge dedupe and spoof the review card), lowercased, trimmed,
 * whitespace-collapsed, capped at PRODUCT_ALIAS_TEXT_MAX code points (Array.from
 * slices by code point so the cap can't strand half a surrogate pair). Every alias
 * comparison happens on this form.
 */
export function normalizeAliasText(raw: string): string {
  return Array.from(
    raw
      .replace(/[\u200B-\u200F\u202A-\u202E\u2060-\u2064\uFEFF]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase(),
  ).slice(0, PRODUCT_ALIAS_TEXT_MAX).join("").trim();
}

/**
 * Normalize whatever is stored in products.aliases into clean labeled entries:
 * plain strings become manual+active, malformed entries are dropped, alias text is
 * lowercased/trimmed/whitespace-collapsed and capped, duplicates (by text) keep the
 * first occurrence, and the list is capped at PRODUCT_ALIASES_MAX.
 *
 * Alias text is stored and compared lowercase — matching is deterministic
 * case-insensitive equality, never fuzzy.
 */
export function normalizeProductAliases(raw: unknown): ProductAliasEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: ProductAliasEntry[] = [];
  const seen = new Set<string>();
  for (const entry of raw as ProductAlias[]) {
    let alias: string;
    let source: ProductAliasSource = "manual";
    let status: ProductAliasStatus = "active";
    let at: string | undefined;
    let seenCount: number | undefined;
    if (typeof entry === "string") {
      alias = entry;
    } else if (entry && typeof entry === "object" && typeof entry.alias === "string") {
      alias = entry.alias;
      if (SOURCES.includes(entry.source)) source = entry.source;
      if (STATUSES.includes(entry.status)) status = entry.status;
      if (typeof entry.at === "string") at = entry.at;
      if (typeof entry.seenCount === "number" && entry.seenCount > 0) seenCount = Math.floor(entry.seenCount);
    } else {
      continue;
    }
    alias = normalizeAliasText(alias);
    if (!alias) continue;
    if (seen.has(alias)) continue;
    seen.add(alias);
    out.push({ alias, source, status, ...(at ? { at } : {}), ...(seenCount ? { seenCount } : {}) });
    if (out.length >= PRODUCT_ALIASES_MAX) break;
  }
  return out;
}

/** Lowercased ACTIVE alias texts for a product — the only entries that resolve/match. */
export function activeAliases(raw: unknown): string[] {
  return normalizeProductAliases(raw)
    .filter((e) => e.status === "active")
    .map((e) => e.alias);
}

/**
 * Search predicate for the in-app product searches: true when any ACTIVE alias
 * contains the search text. Callers pass the already-lowercased search term and OR
 * this with their existing name/category matching.
 */
export function aliasMatchesSearch(raw: unknown, searchLower: string): boolean {
  if (!searchLower) return false;
  return activeAliases(raw).some((a) => a.includes(searchLower));
}

/**
 * Build the alias → canonical-product-name lookup for a catalog.
 *
 * Safety rules, in order:
 * - A real product name always wins: an alias that equals any catalog name
 *   (case-insensitive) is ignored outright.
 * - An alias claimed by MORE THAN ONE product is ambiguous and maps to null —
 *   callers must treat null as "do not auto-resolve" (ask the buyer instead).
 */
export function buildAliasMap(
  catalog: Array<{ name: string; aliases?: unknown }>,
): Map<string, string | null> {
  // Collapse internal whitespace to match alias normalization, so a product name
  // with a doubled space is still shielded from another product claiming the
  // single-spaced alias form.
  const namesLower = new Set(catalog.map((p) => p.name.replace(/\s+/g, " ").trim().toLowerCase()));
  const map = new Map<string, string | null>();
  for (const p of catalog) {
    for (const alias of activeAliases(p.aliases)) {
      if (namesLower.has(alias)) continue; // exact product name beats any alias
      const existing = map.get(alias);
      if (existing === undefined) map.set(alias, p.name);
      else if (existing !== p.name) map.set(alias, null); // collision → ambiguous
    }
  }
  return map;
}
